import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PassportCard from "@/components/PassportCard";
import CredibilityGauge from "@/components/CredibilityGauge";
import TierBadge from "@/components/TierBadge";
import BadgeGrid from "@/components/BadgeGrid";
import Timeline from "@/components/Timeline";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, ShieldX, Lock, Star } from "lucide-react";

export default function VerifyDetail() {
  const { ethicId } = useParams();
  const [data, setData] = useState(null);
  const [cert, setCert] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      // ethicId could be: cert UUID OR ethic_id (EC-XXXX-XXXX)
      if (/^[0-9a-f-]{36}$/i.test(ethicId)) {
        try {
          const { data } = await api.get(`/verify/certificate/${ethicId}`);
          setCert(data);
        } catch (e) {
          setNotFound(true);
        }
        return;
      }
      try {
        const { data } = await api.get(`/verify/passport/${ethicId}`);
        setData(data);
      } catch (e) {
        setNotFound(true);
      }
    })();
  }, [ethicId]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-ec-bg">
        <Navbar />
        <div className="max-w-3xl mx-auto px-6 py-20 text-center">
          <ShieldX className="w-12 h-12 text-ec-error mx-auto" />
          <h1 className="font-display text-3xl font-black mt-4">Not found</h1>
          <p className="text-ec-ink-soft mt-2">No record matches <span className="font-mono-plex font-bold">{ethicId}</span></p>
          <Link to="/verify" className="text-ec-emerald font-semibold mt-6 inline-block underline">Try another search</Link>
        </div>
        <Footer />
      </div>
    );
  }

  if (cert) {
    return <CertificateVerifyView res={cert} />;
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-ec-bg">
        <Navbar />
        <div className="max-w-3xl mx-auto px-6 py-20 eyebrow">Verifying…</div>
      </div>
    );
  }

  if (!data.visible) {
    return (
      <div className="min-h-screen bg-ec-bg" data-testid="verify-private">
        <Navbar />
        <div className="max-w-3xl mx-auto px-6 py-20 text-center">
          <Lock className="w-10 h-10 text-ec-ink mx-auto" />
          <h1 className="font-display text-3xl font-black mt-4">{data.full_name}</h1>
          <p className="text-ec-ink-soft mt-2">{data.message}</p>
          <div className="font-mono-plex font-bold mt-4">{data.ethic_id}</div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ec-bg" data-testid="verify-detail-page">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 sm:px-10 py-10 space-y-8">
        <div className="inline-flex items-center gap-2 text-xs eyebrow text-ec-emerald border border-ec-emerald/30 bg-ec-emerald/10 px-3 py-1.5 rounded-full">
          <ShieldCheck className="w-3.5 h-3.5" /> Authentic EthicCred Passport · Verified now
        </div>

        <PassportCard user={data.user} score={data.score} />

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-ec-line border border-ec-line" data-testid="verify-stats">
          <Stat label="Verified employment" v={data.score.counts.experience_certificates ?? 0} />
          <Stat label="Verified education" v={data.score.counts.educations ?? 0} />
          <Stat label="Recommendations" v={data.score.counts.recommendations ?? 0} />
          <Stat label="Conduct certificates" v={data.score.counts.conduct_certificates ?? 0} />
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          <Card className="rounded-sm border-ec-line lg:col-span-5">
            <CardHeader className="border-b border-ec-line">
              <CardTitle className="font-display">Credibility Score</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col items-center py-10 gap-4">
              <CredibilityGauge score={data.score.score} tier={data.score.tier} />
              {data.ranking && (
                <span className="text-xs text-ec-ink-soft" data-testid="verify-ranking">
                  Top {data.ranking.top_percent}% of {data.ranking.total} professionals
                </span>
              )}
            </CardContent>
          </Card>

          <div className="lg:col-span-7 space-y-6">
            <Card className="rounded-sm border-ec-line">
              <CardHeader className="border-b border-ec-line">
                <CardTitle className="font-display">Professional badges</CardTitle>
              </CardHeader>
              <CardContent className="py-6">
                <BadgeGrid badges={data.badges || []} />
              </CardContent>
            </Card>

            <Block title="Employment" empty="No employment records.">
              {data.employments.map((it) => (
                <div key={it.id} className="py-3 border-b border-ec-line last:border-0">
                  <div className="font-display font-semibold">{it.title}</div>
                  <div className="text-sm text-ec-ink-soft">{it.organization} · {it.start_date} – {it.end_date || "Present"}</div>
                </div>
              ))}
            </Block>
            <Block title="Education" empty="No education records.">
              {data.educations.map((it) => (
                <div key={it.id} className="py-3 border-b border-ec-line last:border-0">
                  <div className="font-display font-semibold">{it.degree}</div>
                  <div className="text-sm text-ec-ink-soft">{it.institution} · {it.start_year} – {it.end_year || "—"}</div>
                </div>
              ))}
            </Block>
            <Block title="Certificates" empty="No certificates issued.">
              {data.certificates.map((c) => (
                <div key={c.id} className="py-3 border-b border-ec-line last:border-0 flex items-center justify-between">
                  <div>
                    <div className="font-display font-semibold">{c.title}</div>
                    <div className="text-sm text-ec-ink-soft">{c.issuer_org} · {c.cert_type}</div>
                  </div>
                  {c.revoked ? (
                    <Badge className="bg-ec-error/10 text-ec-error border-ec-error/30">Revoked</Badge>
                  ) : (
                    <Badge className="bg-ec-emerald/10 text-ec-emerald border-ec-emerald/30">Valid</Badge>
                  )}
                </div>
              ))}
            </Block>
            <Block title="Recommendations" empty="No recommendations yet.">
              {data.recommendations.map((r) => (
                <div key={r.id} className="py-3 border-b border-ec-line last:border-0">
                  <div className="flex items-center gap-2 font-display font-semibold">
                    <Star className="w-4 h-4 text-ec-amber" /> {r.author_name}
                  </div>
                  <p className="text-sm italic text-ec-ink-soft mt-1">"{r.message}"</p>
                </div>
              ))}
            </Block>
          </div>
        </div>

        <Card className="rounded-sm border-ec-line">
          <CardHeader className="border-b border-ec-line">
            <CardTitle className="font-display">Career timeline</CardTitle>
          </CardHeader>
          <CardContent className="py-8">
            <Timeline items={data.timeline || []} />
          </CardContent>
        </Card>
      </div>
      <Footer />
    </div>
  );
}

function Block({ title, children, empty }) {
  const arr = Array.isArray(children) ? children : children?.props?.children;
  const hasItems = arr && (Array.isArray(arr) ? arr.length > 0 : true);
  return (
    <Card className="rounded-sm border-ec-line">
      <CardHeader className="border-b border-ec-line">
        <CardTitle className="font-display text-lg">{title}</CardTitle>
      </CardHeader>
      <CardContent className="py-2">
        {hasItems ? children : <div className="py-6 text-center text-sm text-ec-ink-soft">{empty}</div>}
      </CardContent>
    </Card>
  );
}

function CertificateVerifyView({ res }) {
  const c = res.certificate;
  return (
    <div className="min-h-screen bg-ec-bg" data-testid="verify-certificate-view">
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 py-12">
        {res.valid ? (
          <div className="inline-flex items-center gap-2 text-xs eyebrow text-ec-emerald border border-ec-emerald/30 bg-ec-emerald/10 px-3 py-1.5 rounded-full">
            <ShieldCheck className="w-3.5 h-3.5" /> Authentic certificate · Verified now
          </div>
        ) : (
          <div className="inline-flex items-center gap-2 text-xs eyebrow text-ec-error border border-ec-error/30 bg-ec-error/10 px-3 py-1.5 rounded-full">
            <ShieldX className="w-3.5 h-3.5" /> This certificate has been REVOKED
          </div>
        )}
        <div className="bg-ec-soft border border-ec-ink/15 p-10 mt-6 noise-overlay relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none opacity-[0.04] text-[120px] font-display font-black flex items-center justify-center select-none">
            ETHICCRED
          </div>
          <div className="relative">
            <Badge className="bg-ec-emerald/10 text-ec-emerald border-ec-emerald/30 mb-2 rounded-sm">{c.cert_type}</Badge>
            <h1 className="font-display text-3xl font-black mt-2">{c.title}</h1>
            <p className="text-ec-ink-soft text-sm mt-2 font-mono-plex">{c.certificate_no}</p>
            <p className="mt-6 leading-relaxed">{c.description}</p>
            <div className="grid grid-cols-2 gap-6 mt-8 border-t border-dashed border-ec-ink/15 pt-6">
              <Cell label="Recipient" v={`${c.recipient_name} (${c.recipient_ethic_id})`} />
              <Cell label="Issued by" v={c.issuer_org} />
              <Cell label="Issue date" v={c.issue_date} />
              {c.duration && <Cell label="Duration" v={c.duration} />}
            </div>
            <div className="absolute right-0 bottom-[-10px] stamp text-ec-emerald">{res.valid ? "Verified" : "Revoked"}</div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function Cell({ label, v }) {
  return (
    <div>
      <div className="eyebrow">{label}</div>
      <div className="font-display font-semibold mt-1">{v}</div>
    </div>
  );
}

function Stat({ label, v }) {
  return (
    <div className="bg-white p-5">
      <div className="font-display text-3xl font-black tracking-tight">{v}</div>
      <div className="eyebrow mt-1">{label}</div>
    </div>
  );
}
